import { KitRequestLineSchema } from 'types/kitting';
import {
  useLockKitRequestLinesMutation,
  useUnlockKitRequestLinesMutation,
  useRecalculateKitRequestLineAllocationsMutation,
} from 'store/slices/clientV2/kitRequests';
import { DatagridNames, RowAction } from 'types/datagrid';
import { useMemo, useState } from 'react';
import { ALL_APP_IDS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { useDialogManager } from 'hooks/useDialogManager';
import {
  IconEdit,
  IconLock,
  IconLockOpen,
  IconRefresh,
} from '@tabler/icons-react';
import ToggleRecordDialog, {
  ToggleRecordActionTypes,
} from 'ui-component/clientV2/ToggleRecordDialog';
import { makeColumns } from 'views/kitting/Kit/KitRequestLineTable/columns';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { useIsCofactr } from 'hooks/useIsCofactr';
import KitRequestLineDetailPanel from 'views/kitting/Kit/components/KitRequestLineDetailPanel';
import { useKitRequestContext } from 'views/kitting/Kit/KitRequestContext';
import CreateKitRequestLineDialog from 'views/kitting/Kit/components/CreateKitRequestLineDialog';
import UpdateKitRequestLineDialog from 'views/kitting/Kit/components/UpdateKitRequestLineDialog';
import useSnackbar from 'hooks/useSnackbar';

import { KitScrubbingAlert } from 'views/kitting/Kit/components/KitScrubbingAlert';
import _ from 'lodash';
import KitScrubbingDialog from 'views/kitting/Kit/components/KitScrubbingDialog';
import { ALL_RECORD_TYPE_IDS } from 'constants/recordTypes';
import { AllocatedPartsDialog } from 'ui-component/AllocatedPartsDialog';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';
import { findDuplicateV2PartsInRows } from 'views/kitting/Kit/KitRequestLineTable/utils';
import { Alert, AlertTitle, GridLegacy, Typography } from '@mui/material';
import CopyClipboard from 'ui-component/extended/CopyClipboard';

enum KIT_REQUEST_LINES_DIALOGS {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  PURCHASE = 'PURCHASE',
  LOCK = 'LOCK',
  SCRUBBING = 'SCRUBBING',
  ALLOCATED_PARTS = 'ALLOCATED_PARTS',
}

const KitRequestLineTable = () => {
  const { dispatchErrorSnackbar, dispatchSuccessSnackbar } = useSnackbar();
  const { openDialog, closeDialog, isDialogOpen } = useDialogManager(
    Object.values(KIT_REQUEST_LINES_DIALOGS)
  );
  const [selectedKitRequestLine, setSelectedKitRequestLine] =
    useState<DecoratedKitRequestLine | null>(null);
  const [recalculatedLineIds, setRecalculatedLineIds] = useState<string[]>([]);
  const { isCofactr } = useIsCofactr();
  const showAllocationExplainer = useFeature(
    FeatureFlags.tempAllocationExplainer
  ).on;

  const {
    kitRequest,
    decoratedKitRequestLines,
    isFetchingDecoratedKitRequestLines,
    validations,
  } = useKitRequestContext();

  const [
    recalculateKitRequestLineAllocations,
    { isLoading: isRecalculatingKitRequestLineAllocations },
  ] = useRecalculateKitRequestLineAllocationsMutation();

  const handleRecalculateKitRequestLineAllocations = async (
    rows: DecoratedKitRequestLine[]
  ) => {
    try {
      const ids = rows.map((row) => row.id);
      await recalculateKitRequestLineAllocations({
        ids,
      }).unwrap();
      dispatchSuccessSnackbar('Kit line allocations recalculated');
      if (showAllocationExplainer) {
        setRecalculatedLineIds(ids);
        openDialog(KIT_REQUEST_LINES_DIALOGS.ALLOCATED_PARTS);
      }
    } catch (error) {
      dispatchErrorSnackbar(error as string);
    }
  };

  const rowActions: RowAction<DecoratedKitRequestLine>[] = [
    {
      getLabel: () => 'Edit Line',
      onRowClick: (row) => {
        if (row) {
          setSelectedKitRequestLine(row);
          openDialog(KIT_REQUEST_LINES_DIALOGS.EDIT);
        }
      },
      Icon: IconEdit,
      showInRowMenu: true,
      permissionScope: {
        app: ALL_APP_IDS.KITTING,
        accessLevel: appPermissionAccessLevels.edit,
      },
      isRowDisabled: (row) =>
        Boolean(
          (row?.locked && !isCofactr) ||
            (row?.kitRequest?.approvedBy && !isCofactr)
        ),
    },
    {
      getLabel: (row) => (row?.locked ? 'Unlock Line' : 'Lock Line'),
      onRowClick: (row) => {
        if (row) {
          setSelectedKitRequestLine(row);
          openDialog(KIT_REQUEST_LINES_DIALOGS.LOCK);
        }
      },
      getRowIcon: (row) => (row?.locked ? IconLockOpen : IconLock),
      showInRowMenu: true,
      permissionScope: {
        app: ALL_APP_IDS.KITTING,
        accessLevel: appPermissionAccessLevels.edit,
      },
    },
    {
      getLabel: () => 'Recalculate Kit Line Allocations',
      onRowClick: (row) => {
        if (row) {
          handleRecalculateKitRequestLineAllocations([row]);
        }
      },
      onBatchClick: (rows) => {
        if (rows) {
          handleRecalculateKitRequestLineAllocations(rows);
        }
      },
      disabled: isRecalculatingKitRequestLineAllocations,
      Icon: IconRefresh,
      showInRowMenu: true,
      permissionScope: {
        app: ALL_APP_IDS.KITTING,
        accessLevel: appPermissionAccessLevels.edit,
      },
    },
  ];

  const memoizedColumns = useMemo(() => {
    const columns = makeColumns();
    return columns;
  }, []);

  const recalculatedLines = useMemo(
    () =>
      (decoratedKitRequestLines ?? []).filter((line) =>
        recalculatedLineIds.includes(line.id)
      ),
    [decoratedKitRequestLines, recalculatedLineIds]
  );

  const duplicateMpns = useMemo(
    () => findDuplicateV2PartsInRows(decoratedKitRequestLines ?? []),
    [decoratedKitRequestLines]
  );

  return (
    <>
      {kitRequest &&
        (validations.isLoading ||
          _.some(
            Object.values(validations.validations),
            (validation) => validation && validation.lines.length > 0
          )) && (
          <KitScrubbingAlert
            isLoading={validations.isLoading}
            validations={validations.validations}
            sx={{ my: 3 }}
            onClick={() => openDialog(KIT_REQUEST_LINES_DIALOGS.SCRUBBING)}
          />
        )}
      <GridLegacy item xs={12}>
        {duplicateMpns.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>
              Duplicate Part{duplicateMpns.length === 1 ? '' : 's'} Found
            </AlertTitle>
            <Typography>
              The following part
              {duplicateMpns.length === 1 ? ' is' : 's are'} used in multiple
              lines of this kit request:
            </Typography>
            {duplicateMpns.map((mpn) => (
              <Typography key={mpn}>
                {mpn}
                <CopyClipboard input={mpn} label="mpn" />
              </Typography>
            ))}
            <Typography>
              Please double check that this is intentional.
            </Typography>
          </Alert>
        )}
      </GridLegacy>
      <ExtendedDatagrid<DecoratedKitRequestLine>
        recordType={ALL_RECORD_TYPE_IDS.KIT_REQUEST_LINE}
        gridName={DatagridNames.kitRequestLines}
        loading={isFetchingDecoratedKitRequestLines}
        rows={(decoratedKitRequestLines ?? []) as DecoratedKitRequestLine[]}
        columns={memoizedColumns}
        rowActions={rowActions}
        noRows="Click 'Add Line' to add a line to this kit"
        detailPanel
        getDetailPanelContent={({ row }) => (
          <KitRequestLineDetailPanel kitRequestLine={row} />
        )}
        editable
        editLocked={!!kitRequest?.approved}
        newRecordButton={{
          label: 'Add Line',
          onClick: () => {
            openDialog(KIT_REQUEST_LINES_DIALOGS.CREATE);
          },
          disabled: !!kitRequest?.approved && !isCofactr,
          permissionScope: {
            app: ALL_APP_IDS.KITTING,
            accessLevel: appPermissionAccessLevels.edit,
          },
        }}
      />
      <ToggleRecordDialog<
        KitRequestLineSchema,
        typeof useLockKitRequestLinesMutation,
        typeof useUnlockKitRequestLinesMutation
      >
        dialogOpen={isDialogOpen(KIT_REQUEST_LINES_DIALOGS.LOCK)}
        onClose={closeDialog}
        recordName="Kit Line"
        isCurrentlyTrue={Boolean(selectedKitRequestLine?.locked)}
        id={selectedKitRequestLine?.id ?? ''}
        useToggleTrueMutation={useLockKitRequestLinesMutation}
        useToggleFalseMutation={useUnlockKitRequestLinesMutation}
        toggleActionType={ToggleRecordActionTypes.LOCK}
      />
      {kitRequest && (
        <CreateKitRequestLineDialog
          dialogOpen={isDialogOpen(KIT_REQUEST_LINES_DIALOGS.CREATE)}
          onClose={closeDialog}
          kitRequestId={kitRequest?.id}
        />
      )}
      {selectedKitRequestLine && (
        <UpdateKitRequestLineDialog
          dialogOpen={isDialogOpen(KIT_REQUEST_LINES_DIALOGS.EDIT)}
          onClose={closeDialog}
          kitRequestLine={selectedKitRequestLine}
        />
      )}
      <KitScrubbingDialog
        open={isDialogOpen(KIT_REQUEST_LINES_DIALOGS.SCRUBBING)}
        onClose={closeDialog}
      />
      {showAllocationExplainer && (
        <AllocatedPartsDialog
          open={isDialogOpen(KIT_REQUEST_LINES_DIALOGS.ALLOCATED_PARTS)}
          onClose={closeDialog}
          rows={recalculatedLines}
          loading={
            isRecalculatingKitRequestLineAllocations ||
            isFetchingDecoratedKitRequestLines
          }
          kitRequest={kitRequest}
        />
      )}
    </>
  );
};

export default KitRequestLineTable;
